import gql from "graphql-tag";
import { useQuery } from "@vue/apollo-composable";
import type { Ref } from "@vue/composition-api";

type Vars = Record<string, unknown>;
type VarsInput = Vars | Ref<Vars> | (() => Vars);

function useListQuery<TResult>(
  document: ReturnType<typeof gql>,
  variables: VarsInput = {}
) {
  return useQuery<TResult, Vars>(document, variables as any);
}

export const CompaniesListDocument = gql`
  query CompaniesList(
    $paging: OffsetPaging
    $filter: CompanyFilter
    $sorting: [CompanySort!]
  ) {
    companies(paging: $paging, filter: $filter, sorting: $sorting) {
      totalCount
      nodes {
        Id
        UpdatedAt
        Name
        Webpage
        Focus
        Language
        Address
        contact {
          Id
          Interest
          CreatedAt
        }
      }
    }
  }
`;

export const CompanyPilversListDocument = gql`
  query CompanyPilversList(
    $paging: OffsetPaging
    $filter: CompanyPilverFilter
    $sorting: [CompanyPilverSort!]
  ) {
    companyPilvers(paging: $paging, filter: $filter, sorting: $sorting) {
      totalCount
      nodes {
        Id
        UpdatedAt
        Name
        Webpage
        Focus
        Language
        Address
        contact {
          Id
          Interest
          CreatedAt
        }
      }
    }
  }
`;

export const ContractsListDocument = gql`
  query ContractsList(
    $paging: OffsetPaging
    $filter: ContractFilter
    $sorting: [ContractSort!]
  ) {
    contracts(paging: $paging, filter: $filter, sorting: $sorting) {
      totalCount
      nodes {
        Id
        UpdatedAt
        Name
        Description
        Year
        Type
        attachments {
          Id
          Name
        }
      }
    }
  }
`;

export const ContractPilversListDocument = gql`
  query ContractPilversList(
    $paging: OffsetPaging
    $filter: ContractPilverFilter
    $sorting: [ContractPilverSort!]
  ) {
    contractPilvers(paging: $paging, filter: $filter, sorting: $sorting) {
      totalCount
      nodes {
        Id
        UpdatedAt
        Name
        Description
        Year
        Type
        attachments {
          Id
          Name
        }
      }
    }
  }
`;

export const ProjectsListDocument = gql`
  query ProjectsList(
    $paging: OffsetPaging
    $filter: ProjectFilter
    $sorting: [ProjectSort!]
  ) {
    projects(paging: $paging, filter: $filter, sorting: $sorting) {
      totalCount
      nodes {
        Id
        UpdatedAt
        Name
        Type
        projectChange {
          Id
          State
        }
      }
    }
  }
`;

export const WorkersListDocument = gql`
  query WorkersList(
    $paging: OffsetPaging
    $filter: WorkerFilter
    $sorting: [WorkerSort!]
  ) {
    workers(paging: $paging, filter: $filter, sorting: $sorting) {
      totalCount
      nodes {
        Id
        UpdatedAt
        Name
        Phone
        Note
        Type
        Profession
        attachments {
          Id
          Name
        }
      }
    }
  }
`;

export const MiddlemenListDocument = gql`
  query MiddlemenList(
    $paging: OffsetPaging
    $filter: MiddlemanFilter
    $sorting: [MiddlemanSort!]
  ) {
    middlemen(paging: $paging, filter: $filter, sorting: $sorting) {
      totalCount
      nodes {
        Id
        UpdatedAt
        Name
        Phone
        Note
        attachments {
          Id
          Name
        }
      }
    }
  }
`;

export const CarsListDocument = gql`
  query CarsList(
    $paging: OffsetPaging
    $filter: CarFilter
    $sorting: [CarSort!]
  ) {
    cars(paging: $paging, filter: $filter, sorting: $sorting) {
      totalCount
      nodes {
        Id
        UpdatedAt
        Name
        Evidence
        Description
        Type
        attachments {
          Id
          Name
        }
      }
    }
  }
`;

export const CompanyNamesListDocument = gql`
  query CompanyNamesList(
    $paging: OffsetPaging
    $filter: CompanyFilter
    $sorting: [CompanySort!]
  ) {
    companies(paging: $paging, filter: $filter, sorting: $sorting) {
      nodes {
        Id
        Name
      }
    }
  }
`;

export const CompanyPilverNamesListDocument = gql`
  query CompanyPilverNamesList(
    $paging: OffsetPaging
    $filter: CompanyPilverFilter
    $sorting: [CompanyPilverSort!]
  ) {
    companyPilvers(paging: $paging, filter: $filter, sorting: $sorting) {
      nodes {
        Id
        Name
      }
    }
  }
`;

export const ProjectNamesListDocument = gql`
  query ProjectNamesList(
    $paging: OffsetPaging
    $filter: ProjectFilter
    $sorting: [ProjectSort!]
  ) {
    projects(paging: $paging, filter: $filter, sorting: $sorting) {
      nodes {
        Id
        Name
      }
    }
  }
`;

export type CompaniesListQuery = {
  companies: {
    totalCount: number;
    nodes: Array<{
      Id: string;
      UpdatedAt: string;
      Name: string;
      Webpage?: string | null;
      Focus?: string | null;
      Language?: string | null;
      Address?: string | null;
      contact?: {
        Id: string;
        Interest?: string | null;
        CreatedAt?: string | null;
      } | null;
    }>;
  };
};

export type CompanyPilversListQuery = {
  companyPilvers: CompaniesListQuery["companies"];
};

export type ContractsListQuery = {
  contracts: {
    totalCount: number;
    nodes: Array<{
      Id: string;
      UpdatedAt: string;
      Name: string;
      Description?: string | null;
      Year?: number | null;
      Type?: string | null;
      attachments: Array<{ Id: string; Name: string }>;
    }>;
  };
};

export type ContractPilversListQuery = {
  contractPilvers: ContractsListQuery["contracts"];
};

export type ProjectsListQuery = {
  projects: {
    totalCount: number;
    nodes: Array<{
      Id: string;
      UpdatedAt: string;
      Name: string;
      Type?: string | null;
      projectChange?: { Id: string; State?: string | null } | null;
    }>;
  };
};

export type WorkersListQuery = {
  workers: {
    totalCount: number;
    nodes: Array<{
      Id: string;
      UpdatedAt: string;
      Name: string;
      Phone?: string | null;
      Note?: string | null;
      Type?: string | null;
      Profession?: string | null;
      attachments: Array<{ Id: string; Name: string }>;
    }>;
  };
};

export type MiddlemenListQuery = {
  middlemen: {
    totalCount: number;
    nodes: Array<{
      Id: string;
      UpdatedAt: string;
      Name: string;
      Phone?: string | null;
      Note?: string | null;
      attachments: Array<{ Id: string; Name: string }>;
    }>;
  };
};

export type CarsListQuery = {
  cars: {
    totalCount: number;
    nodes: Array<{
      Id: string;
      UpdatedAt: string;
      Name: string;
      Evidence?: string | null;
      Description?: string | null;
      Type?: string | null;
      attachments: Array<{ Id: string; Name: string }>;
    }>;
  };
};

export function useCompaniesListQuery(variables: VarsInput = {}) {
  return useListQuery<CompaniesListQuery>(CompaniesListDocument, variables);
}

export function useCompanyPilversListQuery(variables: VarsInput = {}) {
  return useListQuery<CompanyPilversListQuery>(
    CompanyPilversListDocument,
    variables
  );
}

export function useContractsListQuery(variables: VarsInput = {}) {
  return useListQuery<ContractsListQuery>(ContractsListDocument, variables);
}

export function useContractPilversListQuery(variables: VarsInput = {}) {
  return useListQuery<ContractPilversListQuery>(
    ContractPilversListDocument,
    variables
  );
}

export function useProjectsListQuery(variables: VarsInput = {}) {
  return useListQuery<ProjectsListQuery>(ProjectsListDocument, variables);
}

export function useWorkersListQuery(variables: VarsInput = {}) {
  return useListQuery<WorkersListQuery>(WorkersListDocument, variables);
}

export function useMiddlemenListQuery(variables: VarsInput = {}) {
  return useListQuery<MiddlemenListQuery>(MiddlemenListDocument, variables);
}

export function useCarsListQuery(variables: VarsInput = {}) {
  return useListQuery<CarsListQuery>(CarsListDocument, variables);
}

export function useCompanyNamesListQuery(variables: VarsInput = {}) {
  return useListQuery<{
    companies: { nodes: Array<{ Id: string; Name: string }> };
  }>(CompanyNamesListDocument, variables);
}

export function useCompanyPilverNamesListQuery(variables: VarsInput = {}) {
  return useListQuery<{
    companyPilvers: { nodes: Array<{ Id: string; Name: string }> };
  }>(CompanyPilverNamesListDocument, variables);
}

export function useProjectNamesListQuery(variables: VarsInput = {}) {
  return useListQuery<{
    projects: { nodes: Array<{ Id: string; Name: string }> };
  }>(ProjectNamesListDocument, variables);
}
